import type {
  Dolan,
  View,
  PageDataReferenceDefinition,
  ComponentDescription,
  NodeRedFlow,
  NodeRedFlowTab,
  NodeRedFlowEventNode,
  NodeRedFlowNode,
} from '@sapappgyver/appgyver-descriptors/lib/dolan/v4';
import { Draft, produce } from 'immer';
import omit from 'lodash/omit';
import getViewLogicTabId from '../getViewLogicTabId';
import generateDeterministicNodeId from '../../../utilities/generateDeterministicNodeId';
import { SUBFLOW_ID_LIST_RECORDS, SUBFLOW_ID_RETRIEVE_RECORD, SUBFLOW_ID_SET_DATA_REFERENCE } from '../subflows';
import traverseComponent from '../../components/utils/traverseComponent';
import getComponentLogicTabId from '../getComponentLogicTabId';
import { migrateComponentEvents } from '../../../modules/components';
import getKeys from '../../../utilities/getKeys';

/**
 * Performs auto-generation and cleanup of all the logic in the given DOLAN view.
 *
 * NOTE: The implementation is almost directly copied from the old
 * legacy AngularJS services, particularly:
 *
 * - TabsService.buildTabTrees(…)
 * - FlowsService.ensureTabsInFlows(…)
 * - LogicUtils.generateDataResourceLogic(…)
 * - cleanupUnknownNodes(…)
 */
export default function autoGenerateDolanLogicFlowsForView(
  view: View,
  descriptions?: Record<string, ComponentDescription>,
  dataResources?: Dolan['data_resources'],
): View {
  const tabs = buildTabTrees(view, descriptions, dataResources);
  const newFlows = ensureTabsInFlows(view.flows, tabs);
  return newFlows !== view.flows ? { ...view, flows: newFlows } : view;
}

interface LogicTab {
  id: string;
  label: string;
  startNodes: LogicTabStartNode[];
}

interface LogicTabStartNode {
  id: string;
  type: 'event';
  customOptions: {
    fallbackName: string;
    eventSource: string;
    idType: 'event';
    isConfigurable: boolean;
  };
  children?: LogicTabFlowNode[];
}

interface LogicTabFlowNode {
  id: string;
  type: `subflow:${string}`;
  customOptions: {
    subflow_input_arguments?: Record<string, unknown>;
    wires?: string[][];
  };
  children?: LogicTabFlowNode[];
}

// Based on the old AngularJS implementation `TabsService.buildTabTrees(…)`
function buildTabTrees(
  view: View,
  descriptions?: Record<string, ComponentDescription>,
  dataResources?: Dolan['data_resources'],
): LogicTab[] {
  return [buildPageTab(view), ...buildComponentTabs(view, descriptions), ...buildDataTabs(view, dataResources)];
}

function buildPageTab(page: View): LogicTab {
  const labelPrefix = page.type === 'global' ? 'Global' : 'Page';
  const pageId = page.id;
  const viewLogicTabId = getViewLogicTabId(pageId);
  const mountEventId = `${viewLogicTabId}:onPageDidMount`;
  return {
    id: viewLogicTabId,
    label: `${labelPrefix}: ${page.display_name}`,
    startNodes: [
      {
        id: generateDeterministicNodeId(['view', mountEventId]),
        type: 'event',
        customOptions: {
          fallbackName: page.type === 'global' ? 'App launched' : 'Page mounted',
          eventSource: mountEventId,
          idType: 'event',
          isConfigurable: true,
        },
      },
      ...(page.data?.resources ?? []).map((res): LogicTabStartNode => {
        const eventSource = `${viewLogicTabId}:data:${res.reference_name}:onChange`;
        return {
          id: generateDeterministicNodeId(['view', eventSource]),
          type: 'event',
          customOptions: {
            fallbackName: `Data '${res.reference_name}' changed`,
            eventSource,
            idType: 'event',
            isConfigurable: true,
          },
        };
      }),
    ],
  };
}

function buildDataTabs(page: View, dataResources?: Dolan['data_resources']): LogicTab[] {
  const pageId = page.id;
  const result: LogicTab[] = [];
  const viewLogicTabId = getViewLogicTabId(pageId);
  for (const dataReference of page.data?.resources ?? []) {
    if (dataReference.data_canvas_flow_id) {
      const resourceName = dataReference.resource_name;
      const referenceName = dataReference.reference_name;
      const resourceDefinition = dataResources?.[resourceName];
      const driverConfiguration =
        resourceDefinition && 'driverConfiguration' in resourceDefinition
          ? resourceDefinition.driverConfiguration
          : null;
      const isResourceLogicTiedToFocus = driverConfiguration?.type === 'firestore';
      const pageMountEventSource = `${viewLogicTabId}:onPageDidMount`;
      const pageFocusEventSource = `${viewLogicTabId}:onPageDidFocus`;
      result.push({
        id: dataReference.data_canvas_flow_id,
        label: `Data: ${referenceName}`,
        startNodes: [
          {
            id: generateDeterministicNodeId(['data', referenceName, pageMountEventSource]),
            type: 'event',
            customOptions: {
              fallbackName: page.type === 'global' ? 'App launched' : 'Page mounted',
              eventSource: pageMountEventSource,
              idType: 'event',
              isConfigurable: false,
            },
            // Generate data resource default logic
            children: !isResourceLogicTiedToFocus ? generateDataResourceLogic(pageId, dataReference) : [],
          },
          ...(page.type === 'global'
            ? []
            : [
                {
                  // For non-global pages, add "page focus" event
                  id: generateDeterministicNodeId(['data', referenceName, pageFocusEventSource]),
                  type: 'event',
                  customOptions: {
                    fallbackName: 'Page focused',
                    eventSource: pageFocusEventSource,
                    idType: 'event',
                    isConfigurable: false,
                  },
                  children: isResourceLogicTiedToFocus ? generateDataResourceLogic(pageId, dataReference) : [],
                } satisfies LogicTabStartNode,
              ]),
        ],
      });
    }
  }
  return result;
}
/**
 * Traverses through the page's component tree and returns
 * all the (default) logic canvases for all of them, ensuring that they exist.
 *
 * TODO: All of this should be done when component instances are created, not like this.
 */
function buildComponentTabs(page: View, descriptions?: Record<string, ComponentDescription>): LogicTab[] {
  const result: LogicTab[] = [];
  const pageId = page.id;
  traverseComponent(page.layout.root_component, (component) => {
    const componentId = component.id;
    const description = descriptions?.[component.name];
    // The root component should not have logic, so skip it
    if (componentId === page.layout.root_component.id) return;
    const tabId = getComponentLogicTabId(pageId, componentId);
    const startNodes: LogicTabStartNode[] = [];
    // add component events only when this is a new component (tab node does not exist yet in page.flows)
    if (!page.flows?.[tabId]) {
      const eventsByName = migrateComponentEvents(description?.events);
      Object.entries(eventsByName).forEach(([eventName, event]) => {
        const eventSource = `${tabId}:${eventName}`;
        startNodes.push({
          id: generateDeterministicNodeId(['component', eventSource]),
          type: 'event',
          customOptions: {
            eventSource,
            fallbackName: event?.title || `Component ${eventName}`,
            idType: 'event',
            isConfigurable: true,
          },
        });
      });
    }
    result.push({
      id: tabId,
      label: `Component: ${component.display_name}`,
      startNodes,
    });
    if (description?.composite_json) {
      // Composite components also have the "inner" logic canvas
      result.push({
        id: `${tabId}:isolated`,
        label: `Component: ${component.display_name}`,
        startNodes: [],
      });
    }
  });
  return result;
}
/**
 * Returns data canvas logic contents for the given data resource.
 * @param {object} dataReference data resource as in DOLAN page
 */
function generateDataResourceLogic(viewId: number, dataReference: PageDataReferenceDefinition): LogicTabFlowNode[] {
  const actionName = dataReference.action_name;
  if (actionName === 'data.findAll') {
    return generateDataResourceListLogic(viewId, dataReference);
  }
  if (actionName === 'data.find') {
    return generateDataResourceRetrieveLogic(viewId, dataReference);
  }
  if (actionName === 'data.new') {
    return generateDataResourceInitLogic(viewId, dataReference);
  }
  return [];
}
/**
 * Generates default logic flow for fetching "ALL" data resources.
 * Used by `generateDataResourceLogic`
 */
function generateDataResourceListLogic(viewId: number, resource: PageDataReferenceDefinition): LogicTabFlowNode[] {
  return generateDataResourceLoadLogic('list', viewId, resource.resource_name, resource.reference_name);
}

/**
 * Generates default logic flow for retrieving "ONE" data resource.
 * Used by `generateDataResourceLogic`
 */
function generateDataResourceRetrieveLogic(viewId: number, resource: PageDataReferenceDefinition): LogicTabFlowNode[] {
  return generateDataResourceLoadLogic('retrieve', viewId, resource.resource_name, resource.reference_name);
}

function generateDataResourceLoadLogic(
  type: 'retrieve' | 'list',
  viewId: number,
  resourceName: string,
  referenceName: string,
): LogicTabFlowNode[] {
  const subflowId = type === 'retrieve' ? SUBFLOW_ID_RETRIEVE_RECORD : SUBFLOW_ID_LIST_RECORDS;
  const outputName = type === 'retrieve' ? 'record' : 'records';
  // Get the new external and local resources
  const loadNodeId = generateDeterministicNodeId(['generateDataResourceLogic', viewId, referenceName, type]);
  return [
    {
      id: loadNodeId,
      // Subflow for retrieving or listing the resource
      type: `subflow:${subflowId}`,
      customOptions: {
        subflow_input_arguments: {
          request: {
            // TODO: parameters / identifier!
            source: {
              resourceName: 'resource',
            },
            key: {
              resourceName,
            },
          },
        },
        wires: [
          [], // `children` shall be added here
          [],
        ],
      },
      children: [
        {
          id: generateDeterministicNodeId(['generateDataResourceLogic', viewId, referenceName, 'setVariable']),
          // "Set data variable"
          type: `subflow:${SUBFLOW_ID_SET_DATA_REFERENCE}`,
          customOptions: {
            subflow_input_arguments: {
              assignment: {
                source: {
                  reference: 'data-reference',
                  data: 'node',
                },
                key: {
                  reference: referenceName,
                  // Previous node output
                  data: `${outputName}@@@${loadNodeId}`,
                },
              },
            },
          },
        },
      ],
    },
  ];
}

/**
 * Generates default logic flow for initializing "NEW" data resource.
 * Used by `generateDataResourceLogic`
 */
function generateDataResourceInitLogic(viewId: number, resource: PageDataReferenceDefinition): LogicTabFlowNode[] {
  // Get the new external and local resources
  const referenceName = resource.reference_name;
  return [
    {
      id: generateDeterministicNodeId(['generateDataResourceLogic', viewId, referenceName, 'setVariable']),
      // "Set data variable"
      type: `subflow:${SUBFLOW_ID_SET_DATA_REFERENCE}`,
      customOptions: {
        subflow_input_arguments: {
          assignment: {
            source: {
              reference: 'data-reference',
              // TODO: Blank bindings
              data: {},
            },
            key: {
              reference: referenceName,
              // TODO: Blank bindings
              data: {},
            },
          },
        },
      },
    },
  ];
}

function ensureTabsInFlows(flows: Record<string, NodeRedFlow>, tabs: LogicTab[]) {
  return initFlowsWithNewTabs(cleanFlowsFromRemovedTabs(flows, tabs), tabs);
}

function initFlowsWithNewTabs(flows: Record<string, NodeRedFlow>, tabs: LogicTab[]): Record<string, NodeRedFlow> {
  return produce(flows, (draftFlows) => {
    tabs.forEach((tab) => {
      const flowTab = tabExists(draftFlows, tab);
      if (flowTab) {
        flowTab.label = tab.label;
      } else {
        draftFlows[tab.id] = { id: tab.id, type: 'tab', label: tab.label };
        tab.startNodes.forEach((startNode, index) => {
          addFlowNode(draftFlows, tab, startNode, 0, index);
        });
      }
    });
  });
}

function tabExists(draftFlows: Draft<Record<string, NodeRedFlow>>, tab: LogicTab): Draft<NodeRedFlowTab> | undefined {
  const node = draftFlows[tab.id];
  return node && node.type === 'tab' ? node : undefined;
}

function addFlowNode(
  draftFlows: Draft<Record<string, NodeRedFlow>>,
  tab: LogicTab,
  node: LogicTabStartNode | LogicTabFlowNode,
  level: number,
  flowIndex: number,
) {
  // ensure we do not start overwriting existing chains
  const match = draftFlows[node.id];
  if (match) return match;

  const flowNode = {
    id: node.id,
    type: node.type,
    x: 100 + level * 250,
    y: 30 + flowIndex * 50,
    z: tab.id,
    wires: [],
    ...node.customOptions,
  } as NodeRedFlowEventNode | NodeRedFlowNode;
  draftFlows[flowNode.id] = flowNode;
  if (node.children) {
    node.children.forEach((childNode) => {
      // eslint-disable-next-line no-param-reassign
      level += 1;
      flowNode.wires[0] = flowNode.wires[0] || [];
      const childFlowNode = addFlowNode(draftFlows, tab, childNode, level, flowIndex);
      flowNode.wires[0].push(childFlowNode.id);
    });
  }
  return flowNode;
}

function cleanFlowsFromRemovedTabs(flows: Record<string, NodeRedFlow>, tabs: LogicTab[]): Record<string, NodeRedFlow> {
  const remainingTabIds = new Set(tabs.map((tab) => tab.id));
  const removedFlowIds = getKeys(flows).filter((flowId) => {
    const flow = flows[flowId];
    if (flow.type === 'tab') {
      // Remove the tab if it's ID is not in preserved tab IDs
      return !remainingTabIds.has(flow.id);
    }
    if ('z' in flow) {
      // Remove any nodes that belong in a non-existing tab
      return !remainingTabIds.has(flow.z);
    }
    // Keep all other nodes
    return false;
  });
  return removedFlowIds.length ? omit(flows, removedFlowIds) : flows;
}
